import { useCallback, useEffect, useState } from "react";
import { api, idr, detailText } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Wallet, Target } from "lucide-react";
import { toast } from "sonner";
import NumberInput from "@/components/app/NumberInput";
import { useT } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

export default function Savings() {
  const { t } = useT();
  const { wedding, refresh } = useAuth();
  // Nama pasangan untuk dropdown "siapa yang nabung"
  const p1Name = wedding?.partner1_nickname || wedding?.partner1_name || "Partner 1";
  const p2Name = wedding?.partner2_nickname || wedding?.partner2_name || "Partner 2";

  const [savings, setSavings] = useState({ entries: [], total: 0, target: 0 });
  const [svOpen, setSvOpen] = useState(false);
  const [svEditingId, setSvEditingId] = useState(null);
  const [svForm, setSvForm] = useState({ amount: 0, date: "", notes: "" });
  // Pilihan penabung: "p1" | "p2" | "custom"
  const [saverChoice, setSaverChoice] = useState("");
  const [svCustomLabel, setSvCustomLabel] = useState("");

  // Target tabungan
  const [targetOpen, setTargetOpen] = useState(false);
  const [targetValue, setTargetValue] = useState(0);
  const [savingTarget, setSavingTarget] = useState(false);

  const loadSavings = useCallback(
    () =>
      api.get("/savings").then(({ data }) => setSavings({ ...data, target: data.target || 0 })).catch((e) => {
        toast.error(detailText(e?.response?.data?.detail) || t("savings.load_error"));
      }),
    [t]
  );
  useEffect(() => { loadSavings(); }, [loadSavings]);

  const total = Number(savings.total || 0);
  const target = Number(savings.target || 0);
  const pct = target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;
  const remaining = Math.max(0, target - total);

  const openAddSavings = () => {
    setSvEditingId(null);
    setSvForm({ amount: 0, date: "", notes: "", fund_type: "wedding" });
    setSaverChoice("");
    setSvCustomLabel("");
    setSvOpen(true);
  };

  const openEditSavings = (entry) => {
    setSvEditingId(entry.entry_id);
    setSvForm({ amount: entry.amount || 0, date: entry.date || "", notes: entry.notes || "", fund_type: entry.fund_type || "wedding" });
    // Cocokkan label lama dengan nama pasangan; kalau bukan keduanya berarti isi sendiri
    const lbl = entry.label || "";
    if (lbl === p1Name) {
      setSaverChoice("p1");
      setSvCustomLabel("");
    } else if (lbl === p2Name) {
      setSaverChoice("p2");
      setSvCustomLabel("");
    } else {
      setSaverChoice(lbl ? "custom" : "");
      setSvCustomLabel(lbl);
    }
    setSvOpen(true);
  };

  const saveSavings = async () => {
    // Resolusi nama penabung dari pilihan dropdown
    let label = "";
    if (saverChoice === "p1") label = p1Name;
    else if (saverChoice === "p2") label = p2Name;
    else if (saverChoice === "custom") label = svCustomLabel.trim();
    if (!label) return toast.error(t("savings.who_required"));
    try {
      if (svEditingId) {
        await api.patch(`/savings/${svEditingId}`, { ...svForm, label });
        toast.success(t("savings.updated"));
      } else {
        await api.post("/savings", { ...svForm, label });
        toast.success(t("savings.added"));
      }
      setSvOpen(false);
      loadSavings();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("savings.save_error"));
    }
  };

  const removeSavings = async (entry) => {
    if (!window.confirm(t("savings.confirm_delete"))) return;
    try {
      await api.delete(`/savings/${entry.entry_id}`);
      toast.success(t("savings.deleted"));
      loadSavings();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("savings.delete_error"));
    }
  };

  const openTarget = () => {
    setTargetValue(target || wedding?.savings_target || 0);
    setTargetOpen(true);
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await api.patch("/wedding", { savings_target: Number(targetValue) || 0 });
      toast.success(t("savings.target_saved"));
      setTargetOpen(false);
      await refresh();
      await loadSavings();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("savings.target_error"));
    } finally { setSavingTarget(false); }
  };

  const fmtDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return d;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("savings.section")}</div>
        <h1 className="font-serif text-4xl">{t("savings.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("savings.subtitle")}</p>
      </div>

      {/* Total + progres target */}
      <Card className="rounded-2xl p-5 md:p-6 mb-6" data-testid="savings-summary">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("savings.total_label")}</div>
            <div data-testid="savings-total" className="font-serif text-3xl mt-1 text-primary">{idr(total)}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={openTarget} data-testid="savings-target-btn">
              <Target className="h-4 w-4 mr-1" /> {t("savings.set_target")}
            </Button>
            <Button className="rounded-full" onClick={openAddSavings} data-testid="savings-add-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("savings.add_btn")}
            </Button>
          </div>
        </div>

        {target > 0 ? (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>{t("savings.target_label")} {idr(target)}</span>
              <span data-testid="savings-pct">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500/70 to-amber-500 transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2" data-testid="savings-remaining">
              {remaining > 0 ? t("savings.remaining") + " " + idr(remaining) : t("savings.reached")}
            </p>
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="savings-no-target">
            <Wallet className="h-4 w-4 shrink-0" />
            {t("savings.no_target")}
          </div>
        )}
      </Card>

      {/* Dialog tambah / edit catatan */}
      <Dialog open={svOpen} onOpenChange={setSvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{svEditingId ? t("savings.edit") : t("savings.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("savings.who")}</Label>
              <Select value={saverChoice} onValueChange={setSaverChoice}>
                <SelectTrigger data-testid="savings-who-select"><SelectValue placeholder={t("savings.who_ph")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="p1">{p1Name}</SelectItem>
                  <SelectItem value="p2">{p2Name}</SelectItem>
                  <SelectItem value="custom">{t("savings.who_custom")}</SelectItem>
                </SelectContent>
              </Select>
              {saverChoice === "custom" && (
                <Input
                  className="mt-2"
                  value={svCustomLabel}
                  onChange={(e) => setSvCustomLabel(e.target.value)}
                  placeholder={t("savings.who_custom_ph")}
                  autoFocus
                  data-testid="savings-custom-label-input"
                />
              )}
            </div>
            <div>
              <Label>Peruntukan Tabungan</Label>
              <Select value={svForm.fund_type || "wedding"} onValueChange={(val) => setSvForm({ ...svForm, fund_type: val })}>
                <SelectTrigger data-testid="savings-fund-type-select"><SelectValue placeholder="Pilih Peruntukan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wedding">Wedding Fund (Pernikahan)</SelectItem>
                  <SelectItem value="honeymoon">Honeymoon Fund (Bulan Madu)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("savings.amount")}</Label><NumberInput prefix="Rp" value={svForm.amount} onChange={(v) => setSvForm({ ...svForm, amount: v })} data-testid="savings-amount-input" /></div>
              <div><Label>{t("common.date")}</Label><Input type="date" value={svForm.date || ""} onChange={(e) => setSvForm({ ...svForm, date: e.target.value })} data-testid="savings-date-input" /></div>
            </div>
            <div><Label>{t("savings.notes_opt")}</Label><Input value={svForm.notes || ""} onChange={(e) => setSvForm({ ...svForm, notes: e.target.value })} data-testid="savings-notes-input" /></div>
            <Button onClick={saveSavings} className="w-full rounded-full" data-testid="savings-save-btn">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog target */}
      <Dialog open={targetOpen} onOpenChange={setTargetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("savings.target_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("savings.target_label")}</Label>
              <NumberInput prefix="Rp" value={targetValue} onChange={setTargetValue} data-testid="savings-target-input" />
            </div>
            <p className="text-xs text-muted-foreground">{t("savings.target_hint")}</p>
            <Button onClick={saveTarget} disabled={savingTarget} className="w-full rounded-full" data-testid="savings-target-save-btn">
              {savingTarget ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {(savings.entries || []).length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-2xl">{t("savings.empty")}</Card>
      ) : (
        <Card className="rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">{t("savings.label")}</th>
                  <th className="text-left px-4 py-3">Peruntukan</th>
                  <th className="text-left px-4 py-3">{t("common.date")}</th>
                  <th className="text-left px-4 py-3">{t("common.notes")}</th>
                  <th className="text-right px-4 py-3">{t("savings.amount")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(savings.entries || []).map((en) => (
                  <tr key={en.entry_id} className="border-t border-border" data-testid="savings-row">
                    <td className="px-4 py-3 font-medium">{en.label}</td>
                    <td className="px-4 py-3">
                      {en.fund_type === "honeymoon" ? (
                        <span className="rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 px-2 py-0.5 text-[10px] font-bold border border-blue-200 dark:border-blue-800">
                          Honeymoon Fund
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 px-2 py-0.5 text-[10px] font-bold border border-amber-200 dark:border-amber-800">
                          Wedding Fund
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{fmtDate(en.date)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{en.notes || "-"}</td>
                    <td className="px-4 py-3 text-right font-medium text-primary">{idr(en.amount)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEditSavings(en)} title={t("savings.edit")}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => removeSavings(en)}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
